﻿import { HubConnectionState } from "@microsoft/signalr";
import { useState, useEffect, useMemo, memo } from "react";
import { Skeleton, Paper, Autocomplete, TextField, IconButton } from "@mui/material";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";
import { useQuery } from '@tanstack/react-query';
import { faChartColumn, faXmark, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { DotItemDotProps, LegendPayload, MouseHandlerDataParam, TooltipContentProps, TooltipPayloadEntry } from "recharts";

import { dashboardsAjax } from "../../../ajax/DashboardsAjax";
import { useSignalRContext } from "../../Contexts/SignalRContext";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { PlantTypeDTO } from "../../../DTOs/PlantTypeDTO";
import { plantAjax } from "../../../ajax/PlantAjax";
import useShiftTimes from "../../Hooks/useShiftTimes";
import { ShiftLockState } from "../../../utilities/helpers/shiftLockStateHelpers";
import { LiveIndicatorState } from "../DashboardCard";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { useJustUpdated } from "../../Hooks/useJustUpdated";
import {
  HOURLY_METRIC_COLOUR,
  HOURLY_METRIC_TARGETS,
  hourlyMetricSeries,
  buildHourDetailRows,
  buildHourlyMetricsChartData,
  formatHourlyMetric,
  hourlyMetricsReferenceMillis,
  pickDefaultPlantType,
  togglePinnedHourIndex,
} from "./hourlyMetricsChartData";
import { AccessLink } from "../../AccessLink/AccessLink";

interface LiveDotProps extends Partial<DotItemDotProps> {
  color: string;
  isEdge: boolean;
  showLive: boolean;
  pulse: boolean;
  labelDy?: number;
}

const LiveDot = (props: LiveDotProps) => {
  const { cx, cy, color, isEdge, showLive, pulse, value, labelDy } = props;
  if (cx == null || cy == null) return <g />;

  if (!showLive || !isEdge) {
    return <circle cx={cx} cy={cy} r={3} fill="var(--theme-surface-2)" stroke={color} strokeWidth={1} />;
  }

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill="none"
        stroke="var(--theme-success)"
        strokeWidth={2}
        className="dashboard-live-ping-idle"
      />
      {pulse && (
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill="none"
          stroke="var(--theme-success)"
          strokeWidth={2}
          className="dashboard-live-ping"
        />
      )}
      <circle
        cx={cx}
        cy={cy}
        r={4.5}
        fill={color}
        stroke="var(--theme-success)"
        strokeWidth={1.5}
        strokeOpacity={0.4}
      />
      {value != null && (
        <text
          x={cx + 9}
          y={cy + (labelDy ?? -8)}
          fontSize={12}
          fontWeight={700}
          fill={color}
          className={pulse ? "dashboard-live-label-pulse" : ""}
        >
          {`${Math.round(value * 100)}%`}
        </text>
      )}
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }: Partial<TooltipContentProps>) => {
  if (!active || !payload?.length) return null;

  const recorded = payload.filter((p: TooltipPayloadEntry) => p.value != null);
  if (!recorded.length) return null;

  return (
    <div className="p-3 border rounded shadow bg-theme-surface">
      <p className="font-semibold text-theme-text text-lg">{label}</p>
      <div className="h-[1px] w-[3rem] bg-theme-border mt-1 mb-2"></div>
      {recorded.flatMap((p: TooltipPayloadEntry) => {
        const series = hourlyMetricSeries(String(p.dataKey));
        if (!series) return [];

        return [(
          <p key={String(p.dataKey)} style={{ color: p.color }}>
            {p.name}: {formatHourlyMetric(Number(p.value), series.unit)}
          </p>
        )];
      })}
      <p className="text-theme-text-muted text-xs mt-2 italic">Click to pin this hour</p>
    </div>
  );
};

/**
 * The hover tooltip follows the cursor, so nothing in it can be clicked. Clicking an hour pins its
 * readings here instead, where each one links to the page that records it.
 */
const PinnedHourPanel = ({
  hour,
  rows,
  onClear,
  isFullScreen,
}: {
  hour: string;
  rows: ReturnType<typeof buildHourDetailRows>;
  onClear: () => void;
  isFullScreen?: boolean;
}) => (
  <div className="shrink-0 border-t border-theme-border pt-2 animate-fade-in">
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`font-semibold text-theme-text ${isFullScreen ? "text-xl" : ""}`}>{hour}</span>

      {rows.map(row => {
        const target = HOURLY_METRIC_TARGETS[row.key];
        const label = (
          <>
            <span className="text-theme-text-muted">{row.label}</span>
            <span className="font-bold" style={{ color: row.colour }}>{row.value}</span>
          </>
        );
        const chipClasses = "flex items-center gap-1.5 rounded-lg border border-theme-border bg-theme-surface px-2 py-1 text-sm transition";

        return (
          <AccessLink
            key={row.key}
            target={target ?? null}
            className={chipClasses}
            linkedClassName="cursor-pointer hover:border-theme-focus hover:bg-theme-surface-2"
            linkedAdornment={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[8px] text-theme-text-muted self-start" />}
          >
            {label}
          </AccessLink>
        );
      })}

      <IconButton size="small" aria-label="Unpin hour" onClick={onClear} className="!text-theme-text">
        <FontAwesomeIcon icon={faXmark} className="text-sm" />
      </IconButton>
    </div>
  </div>
);

const ChartLegend = ({ payload, isFullScreen }: { payload?: ReadonlyArray<LegendPayload>; isFullScreen?: boolean }) => (
  <ul className={`flex flex-wrap items-center justify-center gap-x-4 gap-y-1 m-0 p-0 mb-4 text-theme-table-heading-contrast ${isFullScreen
    ? "text-sm sm:text-base lg:text-lg xl:text-2xl compact:text-sm"
    : "text-[10px] sm:text-xs md:text-sm compact:text-[10px]"}`}>
    {payload?.map((entry: LegendPayload) => (
      <li key={entry.value} className="flex items-center gap-1.5">
        <span
          className={entry.type === "line" ? "inline-block w-3 h-0.5 rounded-full" : "inline-block w-2.5 h-2.5 rounded-sm"}
          style={{ backgroundColor: entry.color }}
        />
        <span>{entry.value}</span>
      </li>
    ))}
  </ul>
);

type HourlyMetricsCardProps = {
  isFullScreen?: boolean;
  shiftLockState: ShiftLockState | null;
  onLiveIndicatorChange?: (state: LiveIndicatorState) => void;
};

export const HourlyMetricsCard: React.FC<HourlyMetricsCardProps> = memo(({ isFullScreen, shiftLockState, onLiveIndicatorChange }) => {
  const { selectedDate, selectedShift } = useShiftTimes();
  const { connection } = useSignalRContext();
  const [selectedPlantType, setSelectedPlantType] = useState<PlantTypeDTO | null>(null);

  const isCurrentShift = useMemo(() => {
    if (!shiftLockState || !selectedDate || !selectedShift) return false;

    return (
      shiftLockState.currentShift.dayOrNight === selectedShift.toString()[0] &&
      shiftLockState.currentShift.date.hasSame(selectedDate, "day")
    );
  }, [shiftLockState, selectedDate, selectedShift]);

  useEffect(() => {
    const connected = connection?.state === HubConnectionState.Connected;

    onLiveIndicatorChange?.({
      active: connected,
      showLabel: connected && isCurrentShift,
      tooltipText: isCurrentShift
        ? "Data displayed for the latest shift"
        : "Data displayed for selected shift",
    });
  }, [connection?.state, isCurrentShift, onLiveIndicatorChange]);

  const { data: plantTypes = [], isLoading: plantTypeLoading } = useQuery({
    queryKey: ['dashboard', 'plantTypes'],
    queryFn: async () => {
      const res = await plantAjax.getPlantTypes({ pageNumber: 1, pageSize: 100, jobTypes: 12 });
      if (ErrorResponse.isErrorResponse(res)) return [] as PlantTypeDTO[];
      return res.data;
    },
  });

  const sortedPlantTypes = useMemo(() => [...plantTypes].sort((a, b) => a.name.localeCompare(b.name)), [plantTypes]);

  const activePlantType = selectedPlantType ?? pickDefaultPlantType(sortedPlantTypes);

  const { data: hourlyMetrics = null, isPending: initialLoading, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard', 'hourlyMetrics', selectedDate.toISODate(), selectedShift, activePlantType?.id],
    queryFn: async () => {
      const res = await dashboardsAjax.getHourlyMetricsCardData(selectedDate, selectedShift, activePlantType!.id!);
      if (ErrorResponse.isErrorResponse(res)) return null;
      return res;
    },
    enabled: !!activePlantType?.id && selectedDate.isValid && !!selectedShift,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const { points: chartData, liveEdgeIndex } = useMemo(
    () => buildHourlyMetricsChartData(hourlyMetrics, hourlyMetricsReferenceMillis(dataUpdatedAt), isCurrentShift),
    [hourlyMetrics, dataUpdatedAt, isCurrentShift]
  );

  const [pinnedHourIndex, setPinnedHourIndex] = useState<number | null>(null);
  const shiftKey = `${selectedDate.toISODate()}|${selectedShift}|${activePlantType?.id}`;
  const [prevShiftKey, setPrevShiftKey] = useState(shiftKey);

  if (shiftKey !== prevShiftKey) { // Note: another shift or plant type is a different set of hours, so the pin cannot survive it
    setPrevShiftKey(shiftKey);
    setPinnedHourIndex(null);
  }

  const pinnedPoint = pinnedHourIndex == null ? null : chartData[pinnedHourIndex] ?? null;
  const pinnedRows = useMemo(() => buildHourDetailRows(pinnedPoint), [pinnedPoint]);

  const liveEdge = liveEdgeIndex >= 0 ? chartData[liveEdgeIndex] : null;
  const justUpdated = useJustUpdated(dataUpdatedAt, 2500);
  const showLive = isCurrentShift && liveEdge !== null;

  const noPlantTypes = !plantTypeLoading && sortedPlantTypes.length === 0;
  const isEmpty = noPlantTypes || (!initialLoading && chartData.length === 0);
  const showChartSkeleton = initialLoading && !isEmpty;

  return (
    <Paper className="!shadow-none !border-none !w-full h-full p-4 flex flex-col gap-2 md:gap-6 min-h-0 !bg-theme-surface-2">
      <Autocomplete
        size="small"
        options={sortedPlantTypes}
        loading={plantTypeLoading}
        value={activePlantType}
        onChange={(_, v) => setSelectedPlantType(v)}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => (
          <TextField {...params} label="Plant Type" />
        )}
      />

      <div className="flex-1 min-h-[240px] relative">
        {showChartSkeleton && (
          <div className="animate-fade-in">
            <Skeleton
              variant="rectangular"
              className="absolute inset-0 rounded-lg !w-full !h-full !animate-pulse"
            />
          </div>
        )}

        {isEmpty && (
          <DashboardCardEmptyState
            isFullScreen={isFullScreen}
            icon={faChartColumn}
            title="No hourly metrics"
            subtitle={noPlantTypes
              ? "No plant types are available to display metrics for."
              : "There's no hourly data recorded for the selected plant, date and shift."}
          />
        )}

        {!isEmpty && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%" debounce={400} className="animate-fade-in">
            <ComposedChart
              data={chartData}
              className="cursor-pointer"
              onClick={(state: MouseHandlerDataParam) =>
                setPinnedHourIndex(prev => togglePinnedHourIndex(prev, state?.activeTooltipIndex))}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="displayHour" tick={{ className: "!fill-theme-table-heading-contrast" }} />
              <YAxis
                yAxisId="left"
                label={{ value: 'BCM', angle: -90, position: 'insideLeft', className: "!fill-theme-table-heading-contrast" }}
                tick={{ className: "!fill-theme-table-heading-contrast" }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                label={{ value: '%', angle: -90, position: 'insideRight', className: "!fill-theme-table-heading-contrast" }}
                tick={{ className: "!fill-theme-table-heading-contrast" }}
                domain={[0, 1]}
                tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
              />

              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" content={<ChartLegend isFullScreen={isFullScreen} />} />

              <Bar
                yAxisId="left"
                dataKey="bcmTotal"
                name="BCM Total"
                fill={HOURLY_METRIC_COLOUR.bcmTotal}
              >
                {chartData.map((_, i) => {
                  const isJustUpdatedEdge = showLive && i === liveEdgeIndex && justUpdated;
                  const isPinned = i === pinnedHourIndex;

                  return (
                    <Cell
                      key={i}
                      fill={HOURLY_METRIC_COLOUR.bcmTotal}
                      stroke={isPinned ? "var(--theme-focus)" : isJustUpdatedEdge ? "var(--theme-success)" : undefined}
                      strokeWidth={isPinned || isJustUpdatedEdge ? 2 : 0}
                    />
                  );
                })}
              </Bar>
              <Bar
                yAxisId="left"
                dataKey="bcmPerHour"
                name="Productivity"
                fill={HOURLY_METRIC_COLOUR.bcmPerHour}
              />

              <Line
                yAxisId="right"
                dataKey="utilization"
                name="Utilization"
                stroke={HOURLY_METRIC_COLOUR.utilization}
                connectNulls={false}
                dot={(p: DotItemDotProps) => <LiveDot {...p} color={HOURLY_METRIC_COLOUR.utilization} isEdge={p.index === liveEdgeIndex} showLive={showLive} pulse={justUpdated} labelDy={17} />}
              />
              <Line
                yAxisId="right"
                dataKey="availability"
                name="Availability"
                stroke={HOURLY_METRIC_COLOUR.availability}
                connectNulls={false}
                dot={(p: DotItemDotProps) => <LiveDot {...p} color={HOURLY_METRIC_COLOUR.availability} isEdge={p.index === liveEdgeIndex} showLive={showLive} pulse={justUpdated} labelDy={-9} />}
              />
              <Line
                yAxisId="right"
                dataKey="availabilityForecastPerHour"
                name="Availability Forecast"
                stroke={HOURLY_METRIC_COLOUR.availabilityForecastPerHour}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {!isEmpty && chartData.length > 0 && (
        pinnedPoint ? (
          <PinnedHourPanel
            hour={pinnedPoint.displayHour}
            rows={pinnedRows}
            onClear={() => setPinnedHourIndex(null)}
            isFullScreen={isFullScreen}
          />
        ) : (
          <p className="shrink-0 border-t border-theme-border pt-2 text-theme-text-muted text-sm italic">
            Click an hour to pin its readings and open the pages behind them.
          </p>
        )
      )}
    </Paper>
  );
});